import React, { useEffect, useState } from 'react';
import { X, Users, ListFilter, Clock, ChevronRight, Settings, ArrowDownUp, Layers, Keyboard, Warehouse, Shapes, TrendingUp, Coins, KeyRound, PackageCheck } from 'lucide-react';
import { TagMatHandlingAdmin } from './TagMatHandlingAdmin';
import { TagShortcutsAdmin } from './TagShortcuts';
import { TagStockCustomersAdmin } from './TagStockCustomersAdmin';
import { TagJewelleryTypesAdmin } from './TagJewelleryTypesAdmin';
import { TagUpliftAdmin } from './TagUpliftAdmin';
import { TagPurityAdmin } from './TagPurityAdmin';
import { TagUnlockPhrases } from './TagUnlockPhrases';
import { TagReceivedLog } from './TagReceivedLog';
import { tagThemeClass } from './tagTheme';
import { TagPortalHours } from './TagPortalHours';
import { TagTypeOrder } from './TagTypeOrder';

const TABS = [['users', 'Users', Users], ['rules', 'Tag rules', ListFilter], ['hours', 'Portal hours', Clock], ['types', 'Type order', ArrowDownUp], ['materials', 'Material handling', Layers], ['shortcuts', 'Shortcuts', Keyboard], ['stock', 'Own stock', Warehouse], ['jtypes', 'Jewellery types', Shapes], ['uplift', 'Carat uplift', TrendingUp], ['purity', 'Gold purity %', Coins], ['phrases', 'Unlock phrases', KeyRound], ['received', 'Received log', PackageCheck]];

function OpenCard({ icon: I, title, text, onOpen, testid }) {
  return (
    <button type="button" onClick={onOpen} data-testid={testid}
      className="group flex w-full items-center gap-4 rounded-xl border border-border bg-background p-4 text-left transition-colors hover:border-[hsl(var(--ring))] hover:bg-secondary/50">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><I size={20} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{text}</span>
      </span>
      <ChevronRight size={18} className="text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

// Super admin settings: users, tag rules and the portal's opening hours.
export function TagSettingsDialog({ onClose, onUsers, onRules }) {
  const [tab, setTab] = useState('users');
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !document.querySelector('[data-testid="tagws-users-admin-dialog"],[data-testid="tagws-tag-rules-dialog"]')) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[190] flex items-end justify-center sm:items-center sm:p-4`} data-testid="tagws-settings-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative z-10 flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 sm:rounded-2xl ${tab === 'materials' || tab === 'shortcuts' ? 'sm:max-w-6xl' : 'sm:max-w-3xl'}`}>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2.5 text-base font-semibold text-foreground"><Settings size={18} /> Settings</div>
          <button onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-xl text-muted-foreground hover:bg-secondary" data-testid="tagws-settings-close"><X size={18} /></button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
          <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-border p-2 sm:w-48 sm:flex-col sm:border-b-0 sm:border-r">
            {TABS.map(([id, label, I]) => (
              <button key={id} type="button" onClick={() => setTab(id)} aria-pressed={tab === id} data-testid={`tagws-settings-tab-${id}`}
                className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === id ? 'bg-secondary text-foreground ring-1 ring-[hsl(var(--ring))]/40' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
                <I size={16} /> {label}
              </button>
            ))}
          </nav>
          <div className="min-h-[340px] flex-1 overflow-y-auto p-5">
            {tab === 'users' ? <OpenCard icon={Users} title="Manage users" text="Add users, reset passwords, set companies and permissions" onOpen={onUsers} testid="tagws-settings-open-users" /> : null}
            {tab === 'rules' ? <OpenCard icon={ListFilter} title="Tag rules" text="Rules that change how pieces print and show on tags" onOpen={onRules} testid="tagws-settings-open-rules" /> : null}
            {tab === 'hours' ? <TagPortalHours /> : null}
            {tab === 'types' ? <TagTypeOrder /> : null}
            {tab === 'materials' ? <TagMatHandlingAdmin /> : null}
            {tab === 'shortcuts' ? <TagShortcutsAdmin /> : null}
            {tab === 'stock' ? <TagStockCustomersAdmin /> : null}
            {tab === 'jtypes' ? <TagJewelleryTypesAdmin /> : null}
            {tab === 'uplift' ? <TagUpliftAdmin /> : null}
            {tab === 'purity' ? <TagPurityAdmin /> : null}
            {tab === 'phrases' ? <TagUnlockPhrases /> : null}
            {tab === 'received' ? <TagReceivedLog /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
